import { useMemo } from 'react';
import { Link } from '@/components/general/Link/Link';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { DynamicProfilingQuestionSkeleton } from '@/components/blocks/dynamicProfiling/DynamicProfilingQuestion/DynamicProfilingQuestionSkeleton';
import { DynamicProfilingQuestion } from '@/components/blocks/dynamicProfiling/DynamicProfilingQuestion/DynamicProfilingQuestion';
import { Alert, Grid, Typography, Stack, Button } from '@mui/material';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { useForm } from 'react-hook-form';
import {
    DYNAMIC_PROFILING_GENERAL_PHRASES,
    DYNAMIC_PROFILING_CONTROL_PHRASES,
} from '@/consts/phrases/dynamicProfilingPhrases';
import {
    IDynamicProfilingData,
    IDynamicProfilingPostData,
    IDynamicProfilingQuestion,
} from '@/types/types';
import type { FetchErrorsType } from '@/types/types';
import { QuestionType, QUESTION_TYPE } from '@/types/enums/dynamicProfiling/questionType';
import { isEmpty, isObject } from '@/utils/fp';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

interface DynamicProfilingFormProps {
    profileData?: IDynamicProfilingData;
    formValues?: { [key: string]: string } | undefined;
    errorMessage?: FetchErrorsType | string;
    submitData: (values: Partial<IDynamicProfilingPostData>) => Promise<void>;
    isSurveyEnd?: boolean;
}

const EmptyQuestionsMessage = (props: { message: string }) => (
    <Grid size={12} sx={{ mt: 2, mb: 2 }}>
        <Alert severity="warning" id="warning_message_box">
            {props.message}
        </Alert>
    </Grid>
);

const ControlYourDataButton = (props: { label: string }) => (
    <Link href="/profile/control" passHref>
        <Button
            variant="text"
            size="small"
            startIcon={<AdminPanelSettingsIcon />}
            className="controlYourDataButton"
        >
            {props.label}
        </Button>
    </Link>
);

const isMultipickOrNumericGridWithZeroValueAllowed = (x: IDynamicProfilingQuestion['question']) => {
    const isMultiPick = QUESTION_TYPE.MULTI_PICK === x.questionTypeId;
    const isNumericGrid = QUESTION_TYPE.NUMERIC_GRID === x.questionTypeId;
    return isMultiPick || (isNumericGrid && x.min <= 0 && x.max >= 0);
};

const isMultiPickOrNumericGridWithZeroSumAllowed = (x: IDynamicProfilingQuestion['question']) => {
    const isMultiPick = QUESTION_TYPE.MULTI_PICK === x.questionTypeId;
    return (
        isMultiPick ||
        (isMultipickOrNumericGridWithZeroValueAllowed(x) && x.minSum <= 0 && x.maxSum >= 0)
    );
};

export const DynamicProfilingForm = ({
    profileData,
    formValues,
    errorMessage,
    submitData,
    isSurveyEnd,
}: DynamicProfilingFormProps) => {
    const { t } = usePhrase();

    const stringifiedProfileData = JSON.stringify(profileData);

    const showSubmitIfNoneApplyMessage = useMemo(
        () =>
            profileData &&
            isObject(profileData) &&
            profileData?.questions?.some(
                (x) => x.answers.length && isMultiPickOrNumericGridWithZeroSumAllowed(x.question),
            ),
        [stringifiedProfileData],
    );

    const addUnfilledResponsesAndSubmit = async (data: Partial<IDynamicProfilingPostData>) => {
        const transformedData = Object.entries(data).reduce((acc, [questionAnswerId, value]) => {
            const questionId = questionAnswerId.split('-')[0];
            const question = profileData?.questions.find(
                (question) => question.question.questionId === Number(questionId),
            );
            if (
                question &&
                !value &&
                isMultipickOrNumericGridWithZeroValueAllowed(question.question)
            ) {
                acc[questionAnswerId] = 0;
            } else {
                acc[questionAnswerId] = value;
            }
            return acc;
        }, {} as Partial<IDynamicProfilingPostData>);

        return submitData(transformedData);
    };

    const getDefaultValues = () => {
        if (!profileData || !profileData?.questions) return {};
        const defaultValues: { [key: string]: string } = {};
        profileData.questions.forEach((item) => {
            const { questionId, questionTypeId, isAutoComplete } = item.question;
            const currentType =
                QuestionType[questionTypeId as (typeof QUESTION_TYPE)[keyof typeof QUESTION_TYPE]];
            const setFieldValue = (fieldName: string) => {
                const fieldValue =
                    formValues && fieldName in formValues ? formValues[fieldName] : '';

                defaultValues[fieldName] = fieldValue;
            };
            if (
                currentType === QuestionType[QUESTION_TYPE.SINGLE_PICK] ||
                (currentType === QuestionType[QUESTION_TYPE.MULTI_PICK] && isAutoComplete)
            ) {
                const name = String(questionId);
                setFieldValue(name);
                return;
            }
            item.answers.forEach((answer) => {
                const name = `${questionId}-${answer.answerId}`;
                setFieldValue(name);
            });
        });
        return defaultValues;
    };

    const { control, handleSubmit, formState } = useForm<Partial<IDynamicProfilingPostData>>({
        defaultValues: getDefaultValues(),
        mode: 'onChange',
    });

    const { isSubmitting } = formState;

    return (
        <form onSubmit={handleSubmit(addUnfilledResponsesAndSubmit)}>
            {!isSurveyEnd && (
                <>
                    <Typography variant="h1">
                        {t(DYNAMIC_PROFILING_GENERAL_PHRASES.MAIN_TITLE)}
                    </Typography>
                    {errorMessage && (
                        <Grid size={12} sx={{ mt: 2, mb: 2 }}>
                            <ErrorAlert id="message_box" errorMessage={errorMessage} />
                        </Grid>
                    )}
                </>
            )}
            {!profileData && <DynamicProfilingQuestionSkeleton />}
            {profileData && isEmpty(profileData.questions) && (
                <EmptyQuestionsMessage
                    message={t(DYNAMIC_PROFILING_GENERAL_PHRASES.EMPTY_QUESTIONS) as string}
                />
            )}
            {profileData && !isEmpty(profileData.questions) && (
                <>
                    {profileData.questions.map((item) => {
                        return (
                            <div key={item.question.questionId}>
                                <DynamicProfilingQuestion
                                    data={item}
                                    control={control}
                                    translations={profileData.translations}
                                />
                            </div>
                        );
                    })}
                    <Grid
                        container
                        direction="row"
                        sx={{
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                                px: isSurveyEnd ? 3 : 'initial',
                            }}
                        >
                            <>
                                {showSubmitIfNoneApplyMessage && (
                                    <Typography variant="caption">
                                        {t(DYNAMIC_PROFILING_GENERAL_PHRASES.SELECTION_COMMENT)}
                                    </Typography>
                                )}
                                <Button
                                    id="dynamic-profiling-submit"
                                    size="large"
                                    type="submit"
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className="dynamicProfilingSubmit"
                                >
                                    {t(DYNAMIC_PROFILING_GENERAL_PHRASES.SUBMIT)}
                                </Button>
                            </>
                        </Stack>
                        {isSurveyEnd && (
                            <>
                                {errorMessage && (
                                    <Grid size={12} sx={{ m: 2 }}>
                                        <ErrorAlert
                                            id="message_box"
                                            errorMessage={'errorMessage'}
                                        />
                                    </Grid>
                                )}
                                <Grid
                                    size={12}
                                    sx={{
                                        py: 2,
                                        pl: 3,
                                        borderTop: 1,
                                        borderColor: (theme) => theme.colors.other.text.lightGray,
                                        mt: 2,
                                    }}
                                >
                                    <ControlYourDataButton
                                        label={String(
                                            t(DYNAMIC_PROFILING_CONTROL_PHRASES.CONTROL_LINK),
                                        )}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </>
            )}
            {!isSurveyEnd && (
                <Grid
                    container
                    direction="row"
                    sx={{
                        justifyContent: 'flex-end',
                        mt: 3,
                    }}
                >
                    <ControlYourDataButton
                        label={t(DYNAMIC_PROFILING_CONTROL_PHRASES.CONTROL_LINK, {
                            asString: true,
                        })}
                    />
                </Grid>
            )}
        </form>
    );
};
